import * as React from 'react';
import { cn } from '@/lib/utils';
import { StatusPill } from '@/components/page';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type { ProductAccessModel } from '@/types';

/** App model copy (design `WS.ACCESS`). */
export const ACCESS_MODELS: Record<
  ProductAccessModel,
  { label: string; title: string; desc: string }
> = {
  gated: {
    label: 'Gated',
    title: 'Gated — explicit grants only',
    desc: 'Only users with an active, unexpired grant can sign in. Roles come from each grant.',
  },
  org_members: {
    label: 'Org members',
    title: 'Organization members',
    desc: 'Members of the owning organization sign in with the default role. Grants can add roles on top.',
  },
  open: {
    label: 'Open',
    title: 'Open — any signed-in user',
    desc: 'Any authrz.me account can sign in. Grants are only needed to hand out roles.',
  },
};

export const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const ROLE_NAME_RE = /^[a-z0-9][a-z0-9_-]*$/;
export const PERM_RE = /^[a-z0-9_.-]+:[a-z0-9_.*-]+$/i;

export function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function validUrl(value: string) {
  if (!value) return true;
  try {
    const u = new URL(value);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}

export function hostOf(url: string) {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

/* ── dates ─────────────────────────────────────────────── */

function parseDate(value: string | null | undefined) {
  if (!value) return null;
  const d = new Date(value.includes('T') ? value : value.replace(' ', 'T') + 'Z');
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(value: string | null | undefined) {
  const d = parseDate(value);
  return d
    ? d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';
}

export function fmtDateTime(value: string | null | undefined) {
  const d = parseDate(value);
  return d ? d.toLocaleString() : '—';
}

export function ago(value: string | null | undefined) {
  const d = parseDate(value);
  if (!d) return '—';
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 45) return 'Just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 30) return `${days}d ago`;
  const mo = Math.round(days / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

/** Whole days until a date (negative when past). */
export function daysUntil(value: string | null | undefined) {
  const d = parseDate(value);
  return d ? Math.ceil((d.getTime() - Date.now()) / 864e5) : null;
}

/* ── grants ────────────────────────────────────────────── */

export type GrantState = 'active' | 'expiring' | 'suspended' | 'expired';
const EXPIRING_SOON_DAYS = 30;

export function grantStateOf(grant: {
  is_active?: boolean;
  expires_at?: string | null;
}): GrantState {
  if (grant.is_active === false) return 'suspended';
  const days = daysUntil(grant.expires_at);
  if (days === null) return 'active';
  const d = parseDate(grant.expires_at);
  if (d && d.getTime() <= Date.now()) return 'expired';
  if (days <= EXPIRING_SOON_DAYS) return 'expiring';
  return 'active';
}

export const GRANT_STATE_LABEL: Record<GrantState, string> = {
  active: 'Active',
  expiring: 'Expiring',
  suspended: 'Suspended',
  expired: 'Expired',
};

const GRANT_TONE = { active: 'ok', expiring: 'warn', suspended: 'warn', expired: 'bad' } as const;

export function GrantPill({ state }: { state: GrantState }) {
  return <StatusPill tone={GRANT_TONE[state]}>{GRANT_STATE_LABEL[state]}</StatusPill>;
}

export function initialsOf(name: string | null | undefined, email: string | null | undefined) {
  const source = name?.trim() || email?.split('@')[0] || '?';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

/* ── presentational ────────────────────────────────────── */

/** Letter tile for an app or organization. */
export function AppMark({
  name,
  size = 'md',
  className,
}: {
  name?: string | null;
  size?: 'md' | 'lg';
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'grid shrink-0 place-items-center border border-border bg-foreground/[0.03] font-mono font-bold',
        size === 'lg'
          ? 'h-12 w-12 rounded-[10px] text-[19px]'
          : 'h-[34px] w-[34px] rounded-lg text-sm',
        className
      )}
    >
      {((name || '?').trim()[0] || '?').toUpperCase()}
    </span>
  );
}

/** Mono token chip (roles, permissions, scopes). */
export function Tag({
  children,
  solid,
  title,
  className,
}: {
  children: React.ReactNode;
  solid?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-[22px] items-center whitespace-nowrap rounded-[5px] border px-[7px] font-mono text-[11.5px]',
        solid ? 'border-transparent bg-foreground/[0.06]' : 'border-border bg-card',
        className
      )}
    >
      {children}
    </span>
  );
}

export function AccessTag({ model }: { model?: ProductAccessModel | string | null }) {
  const m = ACCESS_MODELS[(model as ProductAccessModel) || 'gated'] ?? ACCESS_MODELS.gated;
  return <Tag title={m.title}>{m.label}</Tag>;
}

export function TagList({
  items,
  solid,
  empty,
  prefix,
}: {
  items: string[];
  solid?: boolean;
  empty?: React.ReactNode;
  prefix?: string;
}) {
  if (!items.length) return <>{empty ?? <span className="text-xs text-muted-foreground">None</span>}</>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map((t) => (
        <Tag key={t} solid={solid}>
          {prefix}
          {t}
        </Tag>
      ))}
    </div>
  );
}

export function EmptyState({
  icon: Icon,
  title,
  body,
  action,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  body?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="px-5 py-12 text-center">
      <div className="mx-auto mb-3.5 grid h-11 w-11 place-items-center rounded-[10px] border border-dashed border-foreground/25 text-muted-foreground">
        <Icon className="h-5 w-5" />
      </div>
      <h3 className="text-[15px] font-semibold">{title}</h3>
      {body && (
        <p className="mx-auto mb-4 mt-1.5 max-w-[46ch] text-[13.5px] text-muted-foreground">
          {body}
        </p>
      )}
      {action}
    </div>
  );
}

/** Radio / checkbox choice card (design `.choice`). */
export function ChoiceCard({
  type,
  name,
  value,
  checked,
  disabled,
  onChange,
  title,
  description,
  mono,
}: {
  type: 'radio' | 'checkbox';
  name?: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (checked: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-card px-3 py-2.5 hover:border-foreground/30',
        checked && 'border-foreground bg-foreground/[0.03]',
        disabled && 'cursor-default opacity-85'
      )}
    >
      <input
        type={type}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-[3px] h-3.5 w-3.5 accent-[hsl(var(--foreground))]"
      />
      <span className="min-w-0">
        <b className={cn('block text-[13.5px] font-semibold', mono && 'font-mono text-[12.5px]')}>
          {title}
        </b>
        {description && (
          <small className="mt-0.5 block text-xs text-muted-foreground">{description}</small>
        )}
      </span>
    </label>
  );
}

/** Key / value definition list with hairline rows (design `.kv`). */
export function KV({
  rows,
  className,
}: {
  rows: { label: string; value: React.ReactNode }[];
  className?: string;
}) {
  return (
    <dl className={cn('grid grid-cols-1 sm:grid-cols-[160px_minmax(0,1fr)]', className)}>
      {rows.map((r, i) => (
        <React.Fragment key={r.label}>
          <dt
            className={cn(
              'pt-2.5 text-[13px] text-muted-foreground sm:py-2.5',
              i > 0 && 'border-t border-border',
              i === 0 && 'sm:pt-0'
            )}
          >
            {r.label}
          </dt>
          <dd
            className={cn(
              'min-w-0 pb-2.5 pt-0.5 sm:py-2.5',
              i > 0 && 'sm:border-t sm:border-border',
              i === 0 && 'sm:pt-0'
            )}
          >
            {r.value}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

/** Inline field error text. */
export function FieldError({ children }: { children?: React.ReactNode }) {
  if (!children) return null;
  return (
    <p role="alert" className="text-xs text-destructive">
      {children}
    </p>
  );
}

/** Card section header with title, sub and right-aligned action. */
export function CardHead({
  title,
  sub,
  action,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-[18px] py-3.5">
      <div className="min-w-0">
        <h2 className="text-[15px] font-semibold">{title}</h2>
        {sub && <p className="mt-0.5 text-[13px] text-muted-foreground">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

/** Pager footer for client-side lists. */
export function ListFooter({
  page,
  pageCount,
  total,
  pageSize,
  noun,
  onPage,
}: {
  page: number;
  pageCount: number;
  total: number;
  pageSize: number;
  noun: string;
  onPage: (p: number) => void;
}) {
  if (total <= pageSize) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-[18px] py-3 text-[13px] text-muted-foreground">
      <span>
        Showing {start}–{end} of {total} {noun}
      </span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="inline-flex h-[30px] items-center rounded-md border border-input bg-card px-2.5 text-[12.5px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </button>
        <span className="font-mono text-xs tabular-nums text-foreground">
          {page} / {pageCount}
        </span>
        <button
          type="button"
          className="inline-flex h-[30px] items-center rounded-md border border-input bg-card px-2.5 text-[12.5px] font-medium text-foreground hover:bg-accent disabled:opacity-50"
          disabled={page >= pageCount}
          onClick={() => onPage(page + 1)}
        >
          Next
        </button>
      </div>
    </div>
  );
}

/**
 * Full confirmation dialog for destructive / consequential actions (never a toast).
 * The caller closes it after `onConfirm` resolves; it stays open while `pending`.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  pendingLabel,
  danger,
  typeToConfirm,
  pending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  body: React.ReactNode;
  confirmLabel: string;
  pendingLabel?: string;
  danger?: boolean;
  typeToConfirm?: string;
  pending?: boolean;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (!open) setTyped('');
  }, [open]);
  const blocked = !!typeToConfirm && typed !== typeToConfirm;
  return (
    <AlertDialog open={open} onOpenChange={(o) => !pending && onOpenChange(o)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div>{body}</div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        {typeToConfirm && (
          <div className="space-y-2">
            <label htmlFor="confirm-type" className="text-[13px] font-medium">
              Type <code className="rounded bg-foreground/[0.06] px-1 py-0.5 text-xs">{typeToConfirm}</code>{' '}
              to confirm
            </label>
            <Input
              id="confirm-type"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoComplete="off"
              spellCheck={false}
              className="font-mono"
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={danger ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
            disabled={pending || blocked}
            onClick={(e) => {
              e.preventDefault();
              onConfirm();
            }}
          >
            {pending ? (pendingLabel ?? 'Working…') : confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
